import { Loader2 } from 'lucide-react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { PolicyGate } from './components/PolicyGate';
import { ResponsibilitiesGate } from './components/ResponsibilitiesGate';
import { ResponsibilitiesPage } from './pages/ResponsibilitiesPage';
import { Guard } from './components/Guard';
import { useSession } from './lib/session';
import { AreasPage } from './pages/AreasPage';
import { BoardPage } from './pages/BoardPage';
import { DeadLettersPage } from './pages/DeadLettersPage';
import { ComingSoon } from './pages/ComingSoon';
import { FieldPage } from './pages/FieldPage';
import { Track } from './pages/Track';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { NetworkPage } from './pages/NetworkPage';
import { ListsPage } from './pages/ListsPage';
import { NewTicketPage } from './pages/NewTicketPage';
import { StatusPage } from './pages/StatusPage';
import { TechniciansPage } from './pages/TechniciansPage';
import { TicketDetailPage } from './pages/TicketDetailPage';
import { TicketsPage } from './pages/TicketsPage';
import { AuditPage } from './pages/AuditPage';
import { MyWorkPage } from './pages/MyWorkPage';
import { WorkforcePage } from './pages/WorkforcePage';
import { CookieNotice } from './components/CookieNotice';
import { LeavingProvider } from './components/LeavingDialog';
import { AccountPage } from './pages/AccountPage';
import { DrillsPage } from './pages/DrillsPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { TicketReadOnlyPage } from './pages/TicketReadOnlyPage';
import { DashboardPage } from './pages/DashboardPage';
import { ReportsPage } from './pages/ReportsPage';
import { SettingsPage } from './pages/SettingsPage';
import { AccessPage } from './pages/AccessPage';
import { DeveloperPage } from './pages/DeveloperPage';
import { NewsPage } from './pages/NewsPage';
import { BillingPage } from './pages/BillingPage';
import { ArrearsPage } from './pages/ArrearsPage';
import { InventoryListPage } from './pages/InventoryListPage';
import { StockPage } from './pages/StockPage';
import { NetworkDesignPage } from './pages/NetworkDesignPage';
import { ContractTemplatesPage } from './pages/ContractTemplatesPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { PowerPage } from './pages/PowerPage';
import { StockCountPage } from './pages/StockCountPage';
import { PurchaseOrdersPage } from './pages/PurchaseOrdersPage';
import { AccessRequestsPage } from './pages/AccessRequestsPage';
import {
  AccessRefusedPage,
  ActivatePage,
  AwaitingActivationPage,
  AwaitingApprovalPage,
} from './pages/AccessLandingPages';
import { UsersPage } from './pages/UsersPage';
import { SignContractPage } from './pages/SignContractPage';
import { ContractsPage, ContractDetailPage } from './pages/ContractsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { StaffContractsPage } from './pages/StaffContractsPage';
import { LandlordAgreementsPage } from './pages/LandlordAgreementsPage';
import { LandlordAgreementPage } from './pages/LandlordAgreementPage';
import { LandlordSignPage } from './pages/LandlordSignPage';
import { StaffContractPage } from './pages/StaffContractPage';
import { LeadsPage, LeadDetailPage, ReferralsPage, SalesReportPage } from './pages/SalesPage';
import { CustomersPage } from './pages/CustomersPage';
import { HelpPage, WhatsNewPage } from './pages/HelpPage';

/**
 * Everything the whole application needs around it, whether or not anybody is
 * signed in: the leaving-confirmation dialog, and the cookie notice.
 *
 * Both belong out here rather than inside the console shell. The sign-in page
 * and the technician's job link are the two screens a stranger is most likely
 * to land on first, and they are exactly where a cookie notice has to appear.
 */
function Chrome({ children }: { children: React.ReactNode }) {
  return (
    <LeavingProvider>
      {children}
      <CookieNotice />
    </LeavingProvider>
  );
}

export function App() {
  const { user, loading } = useSession();
  /*
   * The path, from the router rather than from `window`.
   *
   * Every branch below used to read `pathname` directly, and
   * that value is not reactive: a `<Link>` changes the URL and re-renders the
   * `<Routes>` *inside* whichever branch already matched, but never re-runs the
   * branch itself. So "Back to sign in" on the access-refused page navigated to
   * `/login`, stayed inside the four-route block that page lives in, matched
   * none of them, and rendered nothing at all — a blank screen halfway through
   * signing in, which is exactly where it was reported.
   *
   * `useLocation` subscribes this component to the router, so the branches are
   * re-evaluated on every navigation and the correct one wins.
   */
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  // The technician surface is public and lives outside the console shell: a
  // link from an SMS must not demand a login (ROLES.md 1).
  if (pathname.startsWith('/t/')) {
    return (
      <Chrome>
        <Routes>
          <Route path="/t/:token" element={<FieldPage />} />
        </Routes>
      </Chrome>
    );
  }

  // Same for the customer's tracking page, and for the same reason: a member of
  // the public following a link from an email has no account and must never be
  // shown a sign-in form.
  if (pathname.startsWith('/track/')) {
    return (
      <Chrome>
        <Routes>
          <Route path="/track/:token" element={<Track />} />
        </Routes>
      </Chrome>
    );
  }

  /**
   * A read-only ticket, openable by anyone holding the link. Above the sign-in
   * check because that is the whole point of it — but it renders for a signed-in
   * reader too, and uses that to offer the report form directly.
   */
  if (pathname.startsWith('/ticket/')) {
    return (
      <Chrome>
        <Routes>
          <Route path="/ticket/:token" element={<TicketReadOnlyPage />} />
        </Routes>
      </Chrome>
    );
  }

  /**
   * Signing a contract, by somebody who has no account and should not need one.
   *
   * Above the sign-in check for the same reason the technician's job link is:
   * demanding that a customer registers before they can agree to buy anything
   * is how a signature never arrives. The token in the URL is the whole
   * authorisation, and it is scoped to exactly one contract.
   */
  /*
   * A landlord's link, for the same reason and with the same shape.
   *
   * They are not a customer and will usually never be one — they are letting
   * us cable their building, not buying internet — so there is even less of a
   * case for an account here than there is for a customer. Outside the signed-
   * in shell entirely: no rail, no session, and a token scoped to one
   * agreement.
   */
  if (pathname.startsWith('/landlord/')) {
    return (
      <Chrome>
        <Routes>
          <Route path="/landlord/:token" element={<LandlordSignPage />} />
        </Routes>
      </Chrome>
    );
  }

  if (pathname.startsWith('/sign/')) {
    return (
      <Chrome>
        <Routes>
          <Route path="/sign/:token" element={<SignContractPage />} />
        </Routes>
      </Chrome>
    );
  }

  /**
   * Setting a password happens without a session, by definition — the person
   * following this link either has no password yet or cannot use the one they
   * have. It sits above the sign-in check for that reason, and stays reachable
   * even when somebody is already signed in as somebody else on that browser.
   */
  if (pathname.startsWith('/set-password/')) {
    return (
      <Chrome>
        <Routes>
          <Route path="/set-password/:token" element={<SetPasswordPage />} />
        </Routes>
      </Chrome>
    );
  }

  /**
   * Where a provider sign-in lands when it does not end in a session.
   *
   * Above the session check, and for the same reason `/set-password` is:
   * everybody arriving here has no session, and two of these four exist
   * precisely for people who cannot have one yet. Behind the check they would
   * every one of them redirect to the sign-in page — the single screen that
   * cannot answer the question they arrived with.
   *
   * `/activate` is in the same group because the link is followed from an email
   * on whatever device happens to be to hand, which is usually not the one
   * holding a session.
   */
  if (
    ['/awaiting-approval', '/awaiting-activation', '/access-refused'].includes(
      pathname,
    ) ||
    pathname.startsWith('/activate/')
  ) {
    return (
      <Chrome>
        <Routes>
          <Route path="/awaiting-approval" element={<AwaitingApprovalPage />} />
          <Route path="/awaiting-activation" element={<AwaitingActivationPage />} />
          <Route path="/access-refused" element={<AccessRefusedPage />} />
          <Route path="/activate/:token" element={<ActivatePage />} />
        </Routes>
      </Chrome>
    );
  }

  if (!user) {
    // The front door: a page that says what this is, with staff sign-in and a customer's tracking box. Signing in
    // itself is /login (and any deep link, which signs in and then opens it).
    return (
      <Chrome>
        {pathname === '/' ? <LandingPage /> : <LoginPage />}
      </Chrome>
    );
  }

  // Replaces the whole console, rather than sitting in front of it: the API
  // refuses every other route while this is owed, so a navigable app behind a
  // prompt would be one where nothing works and nothing says why.
  if (user.mustChangePassword) {
    return (
      <Chrome>
        <ChangePasswordPage />
      </Chrome>
    );
  }

  return (
    <Chrome>
      {/*
        The policy gate wraps everything inside the shell.

        Inside rather than outside, so somebody reading the terms still sees
        the application behind them and knows what they are agreeing to use —
        and so signing out remains reachable from the gate itself.
      */}
      <AppShell>
      {/*
        Two gates, nested in the order they must be answered.
        The terms include the clause that makes anything else binding, so
        duties are agreed after them and not before. Nesting is what enforces
        that: while the terms are outstanding, the inner gate never mounts.
      */}
      <PolicyGate>
      <ResponsibilitiesGate>
      <Routes>
        {/*
          Where "/" goes depends on what you are. A technician wants the job in
          front of them and a salesperson wants their pipeline; neither opens
          the board first, so neither is landed on it.
        */}
        <Route
          path="/"
          element={
            user.role === 'technician' ? (
              <MyWorkPage />
            ) : user.role === 'sales' ? (
              <LeadsPage />
            ) : (
              <BoardPage />
            )
          }
        />
        {/*
          Every route below carries the capability the API enforces on the
          endpoints behind it. Kept in step with NAV in AppShell — the sidebar
          decides what to show, this decides what will actually open.
        */}
        {/* No Guard. Your own account is not a privilege — every signed-in
            role reaches it, including technicians, and the only thing it can
            change is your own password. */}
        <Route path="/account" element={<AccountPage />} />
        <Route path="/my-work" element={<Guard capability="attendance:own"><MyWorkPage /></Guard>} />
        <Route
          path="/field-team"
          element={<Guard capability="attendance:read_all"><WorkforcePage /></Guard>}
        />
        <Route path="/dashboard" element={<Guard capability="report:read"><DashboardPage /></Guard>} />
        <Route path="/reports" element={<Guard capability="report:read"><ReportsPage /></Guard>} />
        <Route path="/list" element={<Guard capability="ticket:read"><TicketsPage /></Guard>} />
        <Route
          path="/deliveries"
          element={<Guard capability="message:resend"><DeadLettersPage /></Guard>}
        />
        <Route path="/tickets/new" element={<Guard capability="ticket:create"><NewTicketPage /></Guard>} />
        <Route path="/tickets/:id" element={<Guard capability="ticket:read"><TicketDetailPage /></Guard>} />
        <Route
          path="/technicians"
          element={<Guard capability="technician:read"><TechniciansPage /></Guard>}
        />
        <Route path="/network" element={<Guard capability="network:read"><NetworkPage /></Guard>} />
        <Route path="/network/design" element={<Guard capability="network:read"><NetworkDesignPage /></Guard>} />
        <Route path="/power" element={<Guard capability="billing:read"><PowerPage /></Guard>} />
        <Route path="/invoices" element={<Guard capability="billing:read"><InvoicesPage /></Guard>} />
        <Route path="/areas" element={<Guard capability="area:read"><AreasPage /></Guard>} />
        <Route
          path="/lists"
          element={<Guard capability="settings:read"><ListsPage /></Guard>}
        />
        <Route path="/status" element={<Guard capability="system:status"><StatusPage /></Guard>} />
        <Route path="/users" element={<Guard capability="user:manage"><UsersPage /></Guard>} />
        <Route path="/access" element={<Guard capability="user:manage"><AccessPage /></Guard>} />
        <Route
          path="/access-requests"
          element={<Guard capability="user:manage"><AccessRequestsPage /></Guard>}
        />
        <Route
          path="/responsibilities"
          element={
            <Guard capability="settings:write">
              <ResponsibilitiesPage />
            </Guard>
          }
        />
        <Route path="/settings" element={<Guard capability="settings:read"><SettingsPage /></Guard>} />
        <Route path="/developer" element={<Guard capability="settings:write"><DeveloperPage /></Guard>} />
        <Route path="/news" element={<Guard capability="news:read"><NewsPage /></Guard>} />

        {/* Billing, stock and purchasing */}
        <Route path="/billing" element={<Guard capability="billing:read"><BillingPage /></Guard>} />
        <Route path="/arrears" element={<Guard capability="report:arrears"><ArrearsPage /></Guard>} />
        <Route path="/stock" element={<Guard capability="stock:read_own"><StockPage /></Guard>} />
        <Route
          path="/stock/list"
          element={<Guard capability="stock:edit_list"><InventoryListPage /></Guard>}
        />
        <Route path="/stock/counts/:id" element={<Guard capability="stock:count"><StockCountPage /></Guard>} />
        <Route path="/purchasing" element={<Guard capability="purchase:read"><PurchaseOrdersPage /></Guard>} />
        <Route path="/audit" element={<Guard capability="audit:read"><AuditPage /></Guard>} />
        {/* ── Contracts and the vault ────────────────────────────────────── */}
        <Route path="/contracts" element={<Guard capability="contract:read"><ContractsPage /></Guard>} />
        {/* Reading is contract:read; the Save button needs contract:template, which
            only an admin holds. */}
        <Route
          path="/contracts/templates"
          element={<Guard capability="contract:read"><ContractTemplatesPage /></Guard>}
        />
        <Route
          path="/contracts/:id"
          element={<Guard capability="contract:read"><ContractDetailPage /></Guard>}
        />
        {/* ── Contracts of employment ─────────────────────────────────────
            Everybody reaches this: the list shows their own contract, and the
            roster of everybody's appears only for HR. Reading one is guarded by
            `own` because the route cannot know whose contract an id belongs to
            until the service has looked — which is where the rule lives. */}
        <Route
          path="/documents"
          element={<Guard capability="document:own"><DocumentsPage /></Guard>}
        />
        <Route
          path="/staff-contracts"
          element={<Guard capability="staff_contract:own"><StaffContractsPage /></Guard>}
        />
        <Route
          path="/staff-contracts/:id"
          element={<Guard capability="staff_contract:own"><StaffContractPage /></Guard>}
        />
        {/* ── Building access ─────────────────────────────────────────────
            A landlord's written permission to put our equipment in their
            building. Read by sales and HR, signed for the business by an
            admin — the landlord's own half is the public /landlord/:token
            page above, because they have no account. */}
        <Route
          path="/landlord-agreements"
          element={<Guard capability="landlord_agreement:read"><LandlordAgreementsPage /></Guard>}
        />
        <Route
          path="/landlord-agreements/:id"
          element={<Guard capability="landlord_agreement:read"><LandlordAgreementPage /></Guard>}
        />
        <Route path="/customers" element={<Guard capability="customer:read"><CustomersPage /></Guard>} />

        {/* ── Selling ────────────────────────────────────────────────────── */}
        <Route path="/leads" element={<Guard capability="lead:read"><LeadsPage /></Guard>} />
        <Route path="/leads/:id" element={<Guard capability="lead:read"><LeadDetailPage /></Guard>} />
        <Route path="/referrals" element={<Guard capability="referral:read"><ReferralsPage /></Guard>} />
        <Route path="/sales" element={<Guard capability="report:sales"><SalesReportPage /></Guard>} />

        {/* No capability on either: help and the changelog belong to everybody,
            and both read nothing from the business. */}
        <Route path="/help" element={<HelpPage />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />

        {/* Practice belongs to everybody: `quiz:play` is held by every role.
            Which roles are *offered* it is a preference in Settings, and the
            page reads it — hiding the rail entry is no answer to somebody who
            has the address. */}
        <Route path="/drills" element={<Guard capability="quiz:play"><DrillsPage /></Guard>} />
        {/*
          The doors somebody already came through.
          `/login`, `/set-password` and the provider landings are all handled
          above — but only when there is no session. Signed in they fell to the
          catch-all below and said "Page not found", which is untrue and alarming:
          the address exists, it is just finished with. It happens whenever
          somebody uses a browser bookmark, or presses Back far enough after
          signing in. Send them where they were going instead.
        */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/signin" element={<Navigate to="/" replace />} />
        <Route path="/forgot-password" element={<Navigate to="/account" replace />} />

        <Route
          path="*"
          element={<ComingSoon title="Page not found" what="That address does not exist." />}
        />
      </Routes>
      </ResponsibilitiesGate>
      </PolicyGate>
      </AppShell>
    </Chrome>
  );
}
